export interface ManagerInfo {
  code?: string;
  name?: string;
  employeeNumber?: string;
}

interface RawManagerSource {
  reportingManager?: ManagerInfo | null;
  reportingManagerCode?: string;
  reportingManagerName?: string;
  reportingManagerNumber?: string;
  reviewingManager?: ManagerInfo | null;
  reviewingManagerCode?: string;
  reviewingManagerName?: string;
  reviewingManagerNumber?: string;
  acceptingAuthority?: ManagerInfo | null;
  acceptingAuthorityCode?: string;
  acceptingAuthorityName?: string;
  acceptingAuthorityNumber?: string;
}

const pickManager = (nested?: ManagerInfo | null, code?: string, name?: string, employeeNumber?: string): ManagerInfo | null => {
  const resolvedCode = nested?.code || code || "";
  const resolvedName = nested?.name || name || "";
  const resolvedNumber = nested?.employeeNumber || employeeNumber || "";
  if (!resolvedCode && !resolvedName && !resolvedNumber) return null;
  return { code: resolvedCode, name: resolvedName, employeeNumber: resolvedNumber };
};

// Accepts either the nested {code, name, employeeNumber} shape (kpi-report/get, *-assessment/get)
// or the flat *Code/*Name/*Number shape (assigned-employees) and normalizes to {code, name, employeeNumber}.
export const normalizeManagers = (source: RawManagerSource = {}) => ({
  reportingManager: pickManager(source.reportingManager, source.reportingManagerCode, source.reportingManagerName, source.reportingManagerNumber),
  reviewingManager: pickManager(source.reviewingManager, source.reviewingManagerCode, source.reviewingManagerName, source.reviewingManagerNumber),
  acceptingAuthority: pickManager(source.acceptingAuthority, source.acceptingAuthorityCode, source.acceptingAuthorityName, source.acceptingAuthorityNumber),
});

const mergeOne = (prev?: ManagerInfo | null, incoming?: ManagerInfo | null): ManagerInfo | null => {
  if (!prev && !incoming) return null;
  return {
    code: incoming?.code || prev?.code || "",
    name: incoming?.name || prev?.name || "",
    employeeNumber: incoming?.employeeNumber || prev?.employeeNumber || "",
  };
};

// Merges freshly-fetched manager info over what's already in state, keeping any
// existing field a response doesn't include (merged per-field, not wholesale).
export const mergeManagerData = (prev: RawManagerSource = {}, source: RawManagerSource = {}) => {
  const incoming = normalizeManagers(source);
  return {
    reportingManager: mergeOne(prev.reportingManager, incoming.reportingManager),
    reviewingManager: mergeOne(prev.reviewingManager, incoming.reviewingManager),
    acceptingAuthority: mergeOne(prev.acceptingAuthority, incoming.acceptingAuthority),
  };
};
